import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { fetchAuthSession } from '@aws-amplify/auth';
import Question from './components/Question';
import TestTimer from './components/TestTimer';
import SERVICE_API_URL from './serviceUrl';

const StartTest = () => {
  const { testId } = useParams();
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]); // Questions for the current page
  const [answers, setAnswers] = useState(new Map()); // Store answers across all pages
  const [pastAnswers, setPastAnswers] = useState(new Map()); // Store past answers from the server
  const [nextPageToken, setNextPageToken] = useState(null);
  const [prevPageToken, setPrevPageToken] = useState(null);
  const [currentPageToken, setCurrentPageToken] = useState(null);
  const [isLastPage, setIsLastPage] = useState(false);
  const [isFirstPage, setIsFirstPage] = useState(true);
  const [prevPageTokens, setPrevPageTokens] = useState(new Map());
  const [loading, setLoading] = useState(true);
  const [testStatus, setTestStatus] = useState('not_started');
  const [showConfirmSubmit, setShowConfirmSubmit] = useState(false);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [currentQuestionStartIndex, setCurrentQuestionStartIndex] = useState(0);
  const [testName, setTestName] = useState('');
  const [suggestedTimeInMinutes, setSuggestedTimeInMinutes] = useState(null);
  const [error, setError] = useState(null);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [testStartTime, setTestStartTime] = useState(null);
  const [testEndTime, setTestEndTime] = useState(null);
  const [testPrice, setTestPrice] = useState(null);
  const [testDescription, setTestDescription] = useState('');
  const [processingPayment, setProcessingPayment] = useState(false);
  const hasStartedFetching = useRef(false);

  // Function to format time for display (UTC to local timezone with relative time)
  const formatTimeForDisplay = (utcTimeString) => {
    if (!utcTimeString) return 'TBD';
    
    try {
      const utcDate = new Date(utcTimeString);
      
      // Format as local time with relative time
      const localTimeString = utcDate.toLocaleString('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
      
      // Calculate relative time
      const now = new Date();
      const diffMs = utcDate.getTime() - now.getTime();
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      
      let relativeTime = '';
      if (diffMs > 0) {
        if (diffDays > 0) {
          relativeTime = ` (in ${diffDays} day${diffDays > 1 ? 's' : ''})`;
        } else if (diffHours > 0) {
          relativeTime = ` (in ${diffHours} hour${diffHours > 1 ? 's' : ''})`;
        }
        // Removed the "soon" case
      } else {
        if (Math.abs(diffDays) > 0) {
          relativeTime = ` (${Math.abs(diffDays)} day${Math.abs(diffDays) > 1 ? 's' : ''} ago)`;
        } else if (Math.abs(diffHours) > 0) {
          relativeTime = ` (${Math.abs(diffHours)} hour${Math.abs(diffHours) > 1 ? 's' : ''} ago)`;
        } else {
          relativeTime = ' (just now)';
        }
      }
      
      return `${localTimeString}${relativeTime}`;
    } catch (error) {
      console.error('Error formatting time:', error);
      return 'TBD';
    }
  };

  // Function to format time for display without relative time
  const formatTimeForDisplayWithoutRelative = (utcTimeString) => {
    if (!utcTimeString) return 'TBD';
    
    try {
      const utcDate = new Date(utcTimeString);
      
      // Format as local time without relative time
      return utcDate.toLocaleString('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Error formatting time:', error);
      return 'TBD';
    }
  };

  // Fetch initial test status and questions
  useEffect(() => {
    let isMounted = true;
    const abortController = new AbortController();
    
    const fetchInitialData = async () => {
      try {
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken?.toString();
        
        const apiUrl = `${SERVICE_API_URL}/prep/testId`;
        
        const response = await axios.get(apiUrl, {
          params: {
            testId: testId,
            nextPageToken: null
          },
          headers: {
            "Authorization": `Bearer ${idToken}`
          },
          signal: abortController.signal
        });

        // Check if component is still mounted
        if (!isMounted) return;

        // Set test window status and times first
        setTestStartTime(response.data.startTime);
        setTestEndTime(response.data.endTime);
        setTestPrice(response.data.price);
        setTestDescription(response.data.testDescription || '');

        // Check if questions are present
        const hasQuestions = response.data.questions && response.data.questions.length > 0;
        
        if (hasQuestions) {
          // Case 1: Questions are present - show test page (existing experience)
          setTotalQuestions(response.data.totalQuestions);
          setTestName(response.data.testName || '');
          setSuggestedTimeInMinutes(response.data.suggestedTimeInMinutes);

          // Store questions if they're available in the initial response
          setQuestions(response.data.questions);
          setNextPageToken(response.data.nextPageToken);
          
          // Store past answers in a Map for easy lookup
          if (response.data.answers) {
            const answersMap = new Map();
            response.data.answers.forEach(answer => {
              answersMap.set(answer.questionId, answer.answeredOptionId);
            });
            setPastAnswers(answersMap);
          }
          
          if (response.data.nextPageToken != null) {
            prevPageTokens.set(response.data.nextPageToken, 1);
            prevPageTokens.set(1, null);
          }
          
          setCurrentPageToken(null);
          setPrevPageToken(null);
          setIsLastPage(response.data.nextPageToken === null);
          setIsFirstPage(true);

          // If we have answers or correctOption, the test is completed
          const isCompleted = response.data.questions.some(q => q.correctOption) || 
                            (response.data.answers && response.data.answers.length > 0);
          
          if (isCompleted) {
            setTestStatus('completed');
            // Process questions and answers for completed test
            handleCompletedTestData(response.data);
          }
        } else {
          // No questions present - check subscription status and startTime logic
          const currentTime = new Date();
          const startTime = response.data.startTime ? new Date(response.data.startTime) : null;
          const isSubscribed = response.data.subscriptionStatus === 'SUBSCRIBED';
          
          if (isSubscribed) {
            // User is subscribed but questions not available - check if test window is open
            if (!startTime || currentTime < startTime) {
              // Case: Subscribed but before test window opens
              setTotalQuestions(response.data.totalQuestions);
              setTestName(response.data.testName || '');
              setSuggestedTimeInMinutes(response.data.suggestedTimeInMinutes);
              setTestStatus('come_back_at_start');
            } else {
              // Case: Subscribed but test window has passed
              setTotalQuestions(response.data.totalQuestions);
              setTestName(response.data.testName || '');
              setSuggestedTimeInMinutes(response.data.suggestedTimeInMinutes);
              setTestStatus('time_window_passed');
            }
          } else {
            // User is not subscribed - check startTime logic
            const shouldShowSubscribe = !startTime || currentTime < startTime;
            
            if (shouldShowSubscribe) {
              // Case: Not subscribed + (startTime null OR currentTime < startTime) - show subscribe page
              setTotalQuestions(response.data.totalQuestions);
              setTestName(response.data.testName || '');
              setSuggestedTimeInMinutes(response.data.suggestedTimeInMinutes);
              setTestStatus('subscription_required');
            } else {
              // Case: Not subscribed + (startTime not null AND currentTime >= startTime) - show subscription time passed
              setTotalQuestions(response.data.totalQuestions);
              setTestName(response.data.testName || '');
              setSuggestedTimeInMinutes(response.data.suggestedTimeInMinutes);
              setTestStatus('subscription_time_passed');
            }
          }
        }
        
        setLoading(false);
      } catch (error) {
        if (error.name === 'CanceledError' || error.message === 'canceled') {
          return;
        }
        
        if (!isMounted) return;
        console.error("StartTest: Error fetching test data:", error);
        setError("Failed to load test data. Please try again later.");
        setLoading(false);
      }
    };

    fetchInitialData();

    // Cleanup function
    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, [testId]);

  const handleCompletedTestData = (data) => {
    setQuestions(data.questions);
    setNextPageToken(data.nextPageToken);
    
    // Store past answers in a Map for easy lookup
    if (data.answers) {
      const answersMap = new Map();
      data.answers.forEach(answer => {
        answersMap.set(answer.questionId, answer.answeredOptionId);
      });
      setPastAnswers(answersMap);
    }
    
    if (data.nextPageToken != null) {
      prevPageTokens.set(data.nextPageToken, 1);
      prevPageTokens.set(1, null);
    }
    
    setCurrentPageToken(null);
    setPrevPageToken(null);
    setIsLastPage(data.nextPageToken === null);
    setIsFirstPage(true);
  };

  // Fetch the questions for the given token
  const fetchQuestions = useCallback(async (token) => {
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString();

    try {
      const response = await axios.get(`${SERVICE_API_URL}/prep/testId`, {
        params: {
          testId: testId,
          nextPageToken: token
        },
        headers: {
          "Authorization": `Bearer ${idToken}`
        }
      });

      setQuestions(response.data.questions);
      setNextPageToken(response.data.nextPageToken);
      
      // Update the question start index based on the token
      if (token === null) {
        setCurrentQuestionStartIndex(0);
      } else {
        setCurrentQuestionStartIndex(parseInt(token));
      }
      
      // Store past answers in a Map for easy lookup
      if (response.data.answers) {
        const answersMap = new Map();
        response.data.answers.forEach(answer => {
          answersMap.set(answer.questionId, answer.answeredOptionId);
        });
        setPastAnswers(answersMap);
      }
      
      if (response.data.nextPageToken != null) {
        if (token === null) {
          prevPageTokens.set(response.data.nextPageToken, 1);
          prevPageTokens.set(1, null);
        } else {
          prevPageTokens.set(response.data.nextPageToken, token);
        }
      }
      
      setCurrentPageToken(token);
      setPrevPageToken(token);
      setIsLastPage(response.data.nextPageToken === null);
      setIsFirstPage(token === null);
      setLoading(false);
      setError(null); // Clear any existing errors

      // Scroll to top after questions are loaded (works for both active and completed tests)
      requestAnimationFrame(() => {
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      });
    } catch (error) {
      console.error("StartTest: Error fetching questions:", error);
      setLoading(false);
      // Extract error message from the response
      const errorMessage = error.response?.data?.message || 
                         error.response?.data?.error ||
                         "Failed to load test questions. Please try again later.";
      setError(errorMessage);
    }
  }, [testId]); // Add testId as dependency

  // When test status changes to in_progress, fetch questions
  useEffect(() => {
    if (testStatus === 'in_progress' && !isRedirecting && !hasStartedFetching.current) {
      // Check if questions are already loaded
      if (questions.length > 0) {
        hasStartedFetching.current = true;
        setLoading(false);
        return;
      }
      
      hasStartedFetching.current = true;
      fetchQuestions(nextPageToken);
    }
  }, [testStatus, isRedirecting, nextPageToken, questions.length]); // Add questions.length to dependencies

  // Reset hasStartedFetching when test status changes
  useEffect(() => {
    if (testStatus === 'not_started') {
      hasStartedFetching.current = false;
    }
  }, [testStatus]);

  const handleStartTest = () => {
    setTestStatus('in_progress');
  };

  const handlePayment = async () => {
    try {
      setProcessingPayment(true);
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      const response = await axios.post(
        `${SERVICE_API_URL}/prep/payment/create-order`,
        null,
        {
          params: { testIdentifier: testId },
          headers: {
            "Authorization": `Bearer ${idToken}`
          }
        }
      );

      const orderData = response.data;
      if (orderData.status === "already-unlocked") {
        navigate(`/start-test/${testId}`);
        return;
      }

      const order = orderData.order;
      if (!order) {
        throw new Error("Payment order was not returned by the backend");
      }
      const parsedOrder = JSON.parse(order);

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: parsedOrder.amount,
        currency: "INR",
        name: "BrainyHoot",
        description: testName || testId,
        order_id: parsedOrder.id,
        handler: function (response) {
          verifyPayment(response, parsedOrder.id);
        },
        theme: {
          color: "#3399cc",
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (error) {
      console.error("Payment failed:", error);
      alert("Payment failed! Try again.");
    } finally {
      setProcessingPayment(false);
    }
  };

  const verifyPayment = async (response, orderId) => {
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString();

    try {
      const verificationResponse = await axios.post(
        `${SERVICE_API_URL}/prep/payment/verify-payment`,
        {
          orderId: orderId,
          testId: testId,
          paymentId: response.razorpay_payment_id,
          signature: response.razorpay_signature,
        },
        {
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${idToken}`
          }
        }
      );

      const verificationData = verificationResponse.data;
      if (verificationData.status === "success") {
        // Refresh the page to get the updated subscription status
        window.location.reload();
      } else {
        alert("Payment verification failed!");
      }
    } catch (error) {
      console.error("Error verifying payment:", error);
      alert("Payment verification failed!");
    }
  };

  const handleAnswer = (questionId, selectedOption) => {
    setAnswers(prevAnswers => {
      const updatedAnswers = new Map(prevAnswers);
      // If the clicked option is already the selected answer, deselect it.
      if (updatedAnswers.get(questionId) === selectedOption.optionId) {
        updatedAnswers.delete(questionId);
      } else {
        // Otherwise, select the new option.
        updatedAnswers.set(questionId, selectedOption.optionId);
      }
      return updatedAnswers;
    });
  };

  const handleTestSubmit = async () => {
    const allAnswers = Array.from(answers.entries()).map(([questionId, answeredOptionId]) => ({
      questionId,
      answeredOptionId,
    }));

    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();  
      await axios.post(`${SERVICE_API_URL}/prep/submit-answers`,
        {
          testIdentifier: testId,
          answers: allAnswers
        },
        {
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${idToken}`
          }
        }
      );

      // Clean up timer on test completion
      sessionStorage.removeItem('testStartTime');
      setTestStatus('completed');
      
      // Navigate to home page with a flag indicating test completion
      navigate('/', { state: { showYouTubeMessage: true, testName: testName } });
    } catch (error) {
      console.error("Error submitting response:", error);
      alert("Failed to submit response");
    }
  };

  // Scroll to top when questions change or when navigating pages
  useEffect(() => {
    if (questions.length > 0 && (testStatus === 'in_progress' || testStatus === 'completed')) {
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      });
    }
  }, [questions, testStatus, currentPageToken, nextPageToken]);

  const handleNextPage = () => {
    if (nextPageToken) {
      fetchQuestions(nextPageToken);
    }
  };

  const handlePrevPage = () => {
    const prevPageToken = prevPageTokens.get(currentPageToken);
    if (prevPageToken === 1) {
      fetchQuestions(null);
    } else {
      fetchQuestions(prevPageToken);
    }
  };

  const getAnswerButtonStyle = (question, option) => {
    const isSelected = answers.get(question.questionId) === option.optionId;
    const pastAnswer = pastAnswers.get(question.questionId);
    const isCorrect = question.correctOption === option.optionId;
    const isCompleted = testStatus === 'completed';
    
    // For completed tests, show correct answers for all questions
    if (isCompleted && question.correctOption) {
      if (pastAnswer && pastAnswer === option.optionId) {
        // This was the user's answer
        return {
          backgroundColor: isCorrect ? 'var(--success)' : 'var(--error)',
          color: 'white',
          border: '2px solid ' + (isCorrect ? 'var(--success)' : 'var(--error)')
        };
      } else if (isCorrect) {
        // This is the correct answer (for unattempted questions or wrong answers)
        return {
          backgroundColor: 'var(--success)',
          color: 'white',
          border: '2px solid var(--success)'
        };
      } else if (pastAnswer && pastAnswer === option.optionId) {
        // This was the user's wrong answer
        return {
          backgroundColor: 'var(--error)',
          color: 'white',
          border: '2px solid var(--error)'
        };
      } else {
        // This is a wrong option (not selected by user, not correct)
        return {
          backgroundColor: 'white',
          color: 'var(--text-secondary)',
          border: '2px solid var(--border-color)',
          opacity: 0.7
        };
      }
    }
    
    // For questions without past answers (new attempt)
    return {
      textAlign: 'left',
      backgroundColor: isSelected ? 'var(--primary-color)' : 'white',
      color: isSelected ? 'white' : 'var(--text-color)',
      border: '2px solid var(--primary-color)'
    };
  };

  // Calculate progress percentage
  const getProgress = () => {
    const answeredCount = answers.size + pastAnswers.size;
    return totalQuestions > 0 ? Math.round((answeredCount / totalQuestions) * 100) : 0;
  };

  if (loading && testStatus === 'in_progress') {
    return (
      <div className="container">
        <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
          <h2>Loading questions...</h2>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container">
        <div className="card" style={{ 
          textAlign: 'center', 
          padding: '40px',
          backgroundColor: 'var(--error-light)',
          border: '1px solid var(--error)'
        }}>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 'var(--spacing-md)'
          }}>
            <h2 style={{ color: 'var(--error)', marginBottom: 'var(--spacing-sm)' }}>
              Error Loading Test
            </h2>
            <p style={{ color: 'var(--text-primary)' }}>{error}</p>
            <button 
              onClick={() => navigate('/my-tests')}
              className="button"
              style={{ marginTop: 'var(--spacing-md)' }}
            >
              Back to Tests
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (testStatus === 'subscription_required') {
    return (
      <div className="container">
        <div className="card">
          {/* Test Header */}
          <div style={{
            borderBottom: '1px solid var(--border-color)',
            marginBottom: 'var(--spacing-xl)',
            paddingBottom: 'var(--spacing-lg)'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--spacing-md)',
              marginBottom: 'var(--spacing-sm)'
            }}>
              <h2 style={{ 
                margin: 0,
                color: 'var(--primary-color)',
                fontSize: 'var(--font-size-2xl)'
              }}>
                {testName}
              </h2>
              <div style={{
                display: 'flex',
                gap: 'var(--spacing-sm)',
                alignItems: 'center'
              }}>
                <span className="badge" style={{ backgroundColor: 'var(--primary-color)' }}>
                  {totalQuestions} Questions
                </span>
                <span className="badge" style={{ backgroundColor: 'var(--secondary-color)' }}>
                  {suggestedTimeInMinutes} Minutes
                </span>
              </div>
            </div>
            <p style={{ 
              margin: 0,
              color: 'var(--text-secondary)',
              fontSize: 'var(--font-size-md)'
            }}>
              {testDescription || 'Test Details'}
            </p>
          </div>

          {/* Subscription Required Message */}
          <div style={{
            backgroundColor: 'var(--warning-light)',
            border: '1px solid var(--warning)',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--spacing-lg)',
            marginBottom: 'var(--spacing-xl)',
            textAlign: 'center'
          }}>
            <h3 style={{ 
              color: 'var(--warning)',
              marginBottom: 'var(--spacing-md)'
            }}>
              {testPrice && testPrice > 0 ? 'Subscription Required' : 'Access Required'}
            </h3>
            <p style={{ 
              color: 'var(--text-primary)',
              marginBottom: 'var(--spacing-lg)'
            }}>
              {testPrice && testPrice > 0 
                ? `This test requires a subscription to access. You can subscribe now to secure your spot${testStartTime && testEndTime ? ` and the test will be open between ${formatTimeForDisplay(testStartTime)} to ${formatTimeForDisplay(testEndTime)}` : ''}.`
                : `This test requires access. Click below to start practicing${testStartTime && testEndTime ? `. The test will be open between ${formatTimeForDisplay(testStartTime)} to ${formatTimeForDisplay(testEndTime)}` : ''}!`
              }
            </p>

            {/* Payment Button */}
            <button 
              onClick={handlePayment}
              className="button"
              style={{
                fontSize: 'var(--font-size-lg)',
                padding: 'var(--spacing-md) var(--spacing-xl)',
                backgroundColor: 'var(--primary-color)',
                color: 'white',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                cursor: 'pointer'
              }}
              disabled={processingPayment}
            >
              {processingPayment ? 'Processing...' : (testPrice && testPrice > 0 
                ? `Subscribe for ₹${testPrice}`
                : 'Start Test'
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (testStatus === 'subscription_time_passed') {
    return (
      <div className="container">
        <div className="card">
          {/* Test Header */}
          <div style={{
            borderBottom: '1px solid var(--border-color)',
            marginBottom: 'var(--spacing-xl)',
            paddingBottom: 'var(--spacing-lg)'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--spacing-md)',
              marginBottom: 'var(--spacing-sm)'
            }}>
              <h2 style={{ 
                margin: 0,
                color: 'var(--primary-color)',
                fontSize: 'var(--font-size-2xl)'
              }}>
                {testName}
              </h2>
              <div style={{
                display: 'flex',
                gap: 'var(--spacing-sm)',
                alignItems: 'center'
              }}>
                <span className="badge" style={{ backgroundColor: 'var(--primary-color)' }}>
                  {totalQuestions} Questions
                </span>
                <span className="badge" style={{ backgroundColor: 'var(--secondary-color)' }}>
                  {suggestedTimeInMinutes} Minutes
                </span>
              </div>
            </div>
            <p style={{ 
              margin: 0,
              color: 'var(--text-secondary)',
              fontSize: 'var(--font-size-md)'
            }}>
              {testDescription || 'Test Details'}
            </p>
          </div>

          <div style={{ 
            backgroundColor: 'var(--error-light)', 
            border: '1px solid var(--error)',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--spacing-lg)',
            marginBottom: 'var(--spacing-xl)'
          }}>
            <h3 style={{ color: 'var(--error)', marginTop: 0 }}>Subscription Time Passed</h3>
            <p style={{ marginBottom: 'var(--spacing-md)' }}>
              The subscription window for this test closed at{' '}
              <strong>{formatTimeForDisplayWithoutRelative(testStartTime)}</strong>.
            </p>
            <p style={{ marginBottom: 0 }}>
              Unfortunately, you can no longer subscribe to or take this test. Please check for other available tests.
            </p>
          </div>

          <button 
            onClick={() => navigate('/my-tests')} 
            className="button"
          >
            Back to Tests
          </button>
        </div>
      </div>
    );
  }

  if (testStatus === 'come_back_at_start') {
    return (
      <div className="container">
        <div className="card">
          {/* Test Header */}
          <div style={{
            borderBottom: '1px solid var(--border-color)',
            marginBottom: 'var(--spacing-xl)',
            paddingBottom: 'var(--spacing-lg)'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--spacing-md)',
              marginBottom: 'var(--spacing-sm)'
            }}>
              <h2 style={{ 
                margin: 0,
                color: 'var(--primary-color)',
                fontSize: 'var(--font-size-2xl)'
              }}>
                {testName}
              </h2>
              <div style={{
                display: 'flex',
                gap: 'var(--spacing-sm)',
                alignItems: 'center'
              }}>
                <span className="badge" style={{ backgroundColor: 'var(--primary-color)' }}>
                  {totalQuestions} Questions
                </span>
                <span className="badge" style={{ backgroundColor: 'var(--secondary-color)' }}>
                  {suggestedTimeInMinutes} Minutes
                </span>
              </div>
            </div>
            <p style={{ 
              margin: 0,
              color: 'var(--text-secondary)',
              fontSize: 'var(--font-size-md)'
            }}>
              {testDescription || 'Test Details'}
            </p>
          </div>

          <div style={{ 
            backgroundColor: 'var(--info-light)', 
            border: '1px solid var(--info)',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--spacing-lg)',
            marginBottom: 'var(--spacing-xl)'
          }}>
            <h3 style={{ color: 'var(--info)', marginTop: 0 }}>Test Not Yet Available</h3>
            <p style={{ marginBottom: 'var(--spacing-md)' }}>
              Great! You're subscribed to this test. However, the test window hasn't opened yet.
            </p>
            {testStartTime && testEndTime && (
              <p style={{ marginBottom: 'var(--spacing-md)' }}>
                <strong>Test Window:</strong><br />
                Start: {formatTimeForDisplay(testStartTime)}<br />
                End: {formatTimeForDisplay(testEndTime)}
              </p>
            )}
            <p style={{ marginBottom: 0 }}>
              Please come back when the test window opens to begin your assessment.
            </p>
          </div>

          <button 
            onClick={() => navigate('/my-tests')} 
            className="button"
          >
            Back to Tests
          </button>
        </div>
      </div>
    );
  }

  if (testStatus === 'time_window_passed') {
    return (
      <div className="container">
        <div className="card">
          {/* Test Header */}
          <div style={{
            borderBottom: '1px solid var(--border-color)',
            marginBottom: 'var(--spacing-xl)',
            paddingBottom: 'var(--spacing-lg)'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--spacing-md)',
              marginBottom: 'var(--spacing-sm)'
            }}>
              <h2 style={{ 
                margin: 0,
                color: 'var(--primary-color)',
                fontSize: 'var(--font-size-2xl)'
              }}>
                {testName}
              </h2>
              <div style={{
                display: 'flex',
                gap: 'var(--spacing-sm)',
                alignItems: 'center'
              }}>
                <span className="badge" style={{ backgroundColor: 'var(--primary-color)' }}>
                  {totalQuestions} Questions
                </span>
                <span className="badge" style={{ backgroundColor: 'var(--secondary-color)' }}>
                  {suggestedTimeInMinutes} Minutes
                </span>
              </div>
            </div>
            <p style={{ 
              margin: 0,
              color: 'var(--text-secondary)',
              fontSize: 'var(--font-size-md)'
            }}>
              {testDescription || 'Test Details'}
            </p>
          </div>

          <div style={{ 
            backgroundColor: 'var(--error-light)', 
            border: '1px solid var(--error)',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--spacing-lg)',
            marginBottom: 'var(--spacing-xl)'
          }}>
            <h3 style={{ color: 'var(--error)', marginTop: 0 }}>Test Window Closed</h3>
            <p style={{ marginBottom: 'var(--spacing-md)' }}>
              The test window for this assessment has ended. The test was available from{' '}
              <strong>{formatTimeForDisplayWithoutRelative(testStartTime)}</strong> to{' '}
              <strong>{formatTimeForDisplayWithoutRelative(testEndTime)}</strong>.
            </p>
            <p style={{ marginBottom: 0 }}>
              Since you were subscribed but didn't take the test within the window, you can no longer access it.
            </p>
          </div>

          <button 
            onClick={() => navigate('/my-tests')} 
            className="button"
          >
            Back to Tests
          </button>
        </div>
      </div>
    );
  }

  if (testStatus === 'not_started') {
    return (
      <div className="container">
        <div className="card">
          {/* Enhanced Test Header */}
          <div style={{
            borderBottom: '1px solid var(--border-color)',
            marginBottom: 'var(--spacing-xl)',
            paddingBottom: 'var(--spacing-lg)'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--spacing-md)',
              marginBottom: 'var(--spacing-sm)'
            }}>
              <h2 style={{ 
                margin: 0,
                color: 'var(--primary-color)',
                fontSize: 'var(--font-size-2xl)'
              }}>
                {testName}
              </h2>
              <div style={{
                display: 'flex',
                gap: 'var(--spacing-sm)',
                alignItems: 'center'
              }}>
                <span className="badge" style={{ backgroundColor: 'var(--primary-color)' }}>
                  {totalQuestions} Questions
                </span>
                <span className="badge" style={{ backgroundColor: 'var(--secondary-color)' }}>
                  {suggestedTimeInMinutes} Minutes
                </span>
              </div>
            </div>
            <p style={{ 
              margin: 0,
              color: 'var(--text-secondary)',
              fontSize: 'var(--font-size-md)'
            }}>
              Ready to begin your assessment? Take your time to read the instructions below.
            </p>
          </div>

          <div style={{ marginBottom: '30px' }}>
            <h3>Test Instructions:</h3>
            <ul style={{ lineHeight: '1.6' }}>
              <li>Read each question carefully.</li>
              <li>You can navigate between pages using Previous and Next buttons.</li>
              <li>Submit the test only when you have answered all questions.</li>
              <li>You cannot change answers after submission.</li>
              <li>The timer shows your progress, but you can keep going even after time runs out.</li>
            </ul>
          </div>
          <button onClick={handleStartTest} className="button">
            Start Test
          </button>
        </div>
      </div>
    );
  }

  if (testStatus === 'completed') {
    return (
      <div className="container">
        {/* Enhanced Results Header */}
        <div style={{ 
          position: 'sticky', 
          top: 0, 
          background: 'white', 
          padding: '15px 0', 
          borderBottom: '1px solid var(--border-color)',
          zIndex: 1000
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: 'var(--spacing-md)'
          }}>
            <div>
              <h2 style={{ 
                margin: 0,
                marginBottom: 'var(--spacing-xs)',
                color: 'var(--primary-color)',
                fontSize: 'var(--font-size-2xl)'
              }}>
                {testName}
              </h2>
              <span className="badge" style={{ 
                backgroundColor: 'var(--primary-color)',
                fontSize: 'var(--font-size-md)'
              }}>
                {totalQuestions} Questions
              </span>
            </div>
            <button 
              onClick={() => navigate('/my-tests')} 
              className="button secondary"
            >
              Back to Tests
            </button>
          </div>
        </div>

        {/* Questions Section */}
        <div className="card" style={{ margin: '20px 0' }}>
          {questions.map((question, index) => (
            <Question
              key={question.questionId}
              question={question}
              onAnswer={() => {}}
              selectedAnswer={pastAnswers.get(question.questionId)}
              isCompleted={true}
              getAnswerButtonStyle={getAnswerButtonStyle}
              questionNumber={currentQuestionStartIndex + index + 1}
            />
          ))}
        </div>

        {/* Navigation Buttons */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          marginTop: '20px'
        }}>
          <button 
            onClick={handlePrevPage}
            className="button secondary"
            disabled={isFirstPage}
          >
            Previous Page
          </button>
          <button 
            onClick={handleNextPage}
            className="button"
            disabled={isLastPage}
          >
            Next Page
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      {/* Enhanced Active Test Header */}
      <div style={{ 
        position: 'sticky', 
        top: 0, 
        background: 'white', 
        padding: '15px 0', 
        borderBottom: '1px solid var(--border-color)',
        zIndex: 1000
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: 'var(--spacing-md)'
        }}>
          <div>
            <h2 style={{ 
              margin: 0,
              marginBottom: 'var(--spacing-xs)',
              color: 'var(--primary-color)',
              fontSize: 'var(--font-size-2xl)'
            }}>
              {testName || 'Loading...'}
            </h2>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              color: 'var(--text-secondary)',
              fontSize: 'var(--font-size-md)'
            }}>
              {answers.size + pastAnswers.size} of {totalQuestions} questions answered ({getProgress()}%)
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            {testStatus === 'in_progress' && suggestedTimeInMinutes && (
              <TestTimer suggestedTimeInMinutes={suggestedTimeInMinutes} />
            )}
            <button 
              onClick={() => setShowConfirmSubmit(true)} 
              className="button secondary"
              disabled={questions.some(q => q.correctOption || pastAnswers.has(q.questionId))}
            >
              Submit Test
            </button>
          </div>
        </div>
        <div className="progress-bar">
          <div 
            className="progress-bar-fill" 
            style={{ 
              width: `${getProgress()}%`,
              transition: 'width 0.3s ease'
            }}
          />
        </div>
      </div>

      {/* Questions Section */}
      <div className="card" style={{ margin: '20px 0' }}>
        {questions.map((question, index) => (
          <Question
            key={question.questionId}
            question={question}
            onAnswer={handleAnswer}
            selectedAnswer={answers.get(question.questionId)}
            isCompleted={testStatus === 'completed'}
            getAnswerButtonStyle={getAnswerButtonStyle}
            questionNumber={currentQuestionStartIndex + index + 1}
          />
        ))}
      </div>

      {/* Navigation Buttons */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between',
        marginTop: '20px'
      }}>
        <button 
          onClick={handlePrevPage}
          className="button secondary"
          disabled={isFirstPage}
        >
          Previous Page
        </button>
        <button 
          onClick={handleNextPage}
          className="button"
          disabled={isLastPage}
        >
          Next Page
        </button>
      </div>

      {/* Submit Confirmation Dialog */}
      {showConfirmSubmit && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="card" style={{ 
            maxWidth: '400px',
            width: '90%',
            textAlign: 'center'
          }}>
            <h3>Submit Test?</h3>
            {(answers.size + pastAnswers.size) < totalQuestions && (
              <p style={{ 
                color: 'var(--text-secondary)',
                margin: 'var(--spacing-md) 0'
              }}>
                You have {totalQuestions - (answers.size + pastAnswers.size)} unanswered questions. Unanswered questions will be marked as incorrect.
              </p>
            )}
            <div style={{ 
              display: 'flex', 
              gap: '10px', 
              justifyContent: 'center',
              marginTop: '20px'
            }}>
              <button 
                onClick={() => setShowConfirmSubmit(false)}
                className="button secondary"
              >
                Continue Test
              </button>
              <button 
                onClick={handleTestSubmit}
                className="button"
              >
                Submit Test
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StartTest;
